import { useEffect, useState } from "react";
import {
  Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton } from "@/components/Skeleton";
import { FlaskConical } from "lucide-react";

/** Lab markers over time.
 *
 *  One calm line per marker with the lab's own reference range as a soft band. No goal
 *  lines and no colour-coding of our own: the only clinical judgement shown is the lab's
 *  `flag`, which we pass through untouched.
 *
 *  Values that cannot be parsed — "<5", "positive", ">1000" — are kept OFF the line and
 *  kept IN the list. A patient must never conclude a result doesn't exist because it
 *  couldn't be plotted, and plotting "<5" as 5 would be a clinical lie.
 */

const SLATE = "#1E293B";

function shortDate(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    month: "short", day: "numeric", timeZone: "UTC",
  });
}

function FlagChip({ flag }) {
  if (!flag || flag === "normal") return null;
  // The lab said this, not us.
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground capitalize"
          data-testid={`lab-flag-${flag}`}>{flag}</span>
  );
}

function Marker({ marker }) {
  const band = marker.reference;
  return (
    <div className="bg-card border border-border rounded-lg p-5"
         data-testid={`lab-marker-${marker.marker.replace(/\s+/g, "-")}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-heading text-base font-semibold">{marker.marker}</h3>
        <span className="text-xs text-muted-foreground">
          {marker.unit}{marker.ref_range ? ` · usual range ${marker.ref_range}` : ""}
        </span>
      </div>

      {marker.chartable ? (
        <div className="h-44 mt-4 -ml-2" data-testid={`lab-chart-${marker.marker.replace(/\s+/g, "-")}`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={marker.points} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
              {band && (
                <ReferenceArea y1={band.low} y2={band.high} fill={SLATE}
                               fillOpacity={0.06} strokeOpacity={0} />
              )}
              <XAxis dataKey="date" tickFormatter={shortDate} tickLine={false}
                     axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis width={44} tickLine={false} axisLine={false}
                     tick={{ fontSize: 11, fill: "#64748B" }} domain={["auto", "auto"]} />
              <Tooltip
                labelFormatter={shortDate}
                formatter={(v) => [`${v} ${marker.unit}`, marker.marker]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E2E8F0" }} />
              <Line type="monotone" dataKey="value" stroke={SLATE} strokeWidth={2}
                    dot={{ r: 3, fill: SLATE }} activeDot={{ r: 4 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mt-3"
           data-testid={`lab-nochart-${marker.marker.replace(/\s+/g, "-")}`}>
          {marker.readings.length > 1
            ? "These results aren't numbers, so there's no line to draw — they're listed below."
            : "One result so far. A line appears once there's more than one to compare."}
        </p>
      )}

      <ul className="mt-3 divide-y divide-border border-t border-border"
          data-testid={`lab-readings-${marker.marker.replace(/\s+/g, "-")}`}>
        {marker.readings.slice().reverse().map((r, i) => (
          <li key={`${r.date}-${i}`} className="py-2 flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{shortDate(r.date)}</span>
            <span className="inline-flex items-center gap-2">
              <span className="font-medium tabular-nums">{r.value} {marker.unit}</span>
              <FlagChip flag={r.flag} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LabTrends({ path }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(path)
      .then((r) => setData(r.data))
      .catch((e) => { setData({ markers: [] }); setError(formatApiError(e.response?.data?.detail)); });
  }, [path]);

  if (!data) return <CardSkeleton lines={4} />;

  if (!data.markers.length) {
    return (
      <EmptyState icon={FlaskConical} title="No results yet"
                  description="Once your labs come back, you'll see each marker tracked here over time."
                  testid="no-lab-trends" />
    );
  }

  return (
    <div className="space-y-4" data-testid="lab-trends">
      {error && (
        <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
      {data.markers.map((m) => <Marker key={m.marker} marker={m} />)}
    </div>
  );
}
